import { api } from "@/lib/api";
import { Flame, Check, X, HandHeart, Megaphone, Award } from "lucide-react";
import { HandleAvatar } from "@/components/HandleAvatar";
import { MILESTONE_LABEL } from "@/hooks/useBuddies";
import { SampleTag } from "@/components/community/SampleTag";

function SharedStreak({ b }) {
  const s = b.stats;
  return (
    <div className="flex items-center gap-4 mt-2 flex-wrap">
      <div className="flex items-center gap-1.5">
        <Flame className={`w-5 h-5 ${s.shared_streak > 0 ? "text-orange-600 fill-orange-300" : "text-stone-300"}`} />
        <span className="font-mono text-2xl font-bold text-amber-800" data-testid={`shared-streak-${b.id}`}>{s.shared_streak}</span>
        <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">shared streak</span>
      </div>
      <div className="text-xs text-stone-600">
        Both showed up <span className="font-bold">{s.both_this_week}</span> of the last 7 days · you {s.me_this_week}, {b.partner.handle} {s.partner_this_week}
      </div>
    </div>
  );
}

function Milestones({ b }) {
  if (!b.milestones?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`buddy-milestones-${b.id}`}>
      {b.milestones.map((m) => (
        <span key={m} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900" data-testid={`milestone-${m}-${b.id}`}>
          <Award className="w-3 h-3" /> {MILESTONE_LABEL[m]}
        </span>
      ))}
    </div>
  );
}

function TodayRow({ b, act }) {
  const s = b.stats;
  return (
    <div className="text-xs mt-2 flex items-center gap-2 flex-wrap">
      <span className={s.me_today ? "text-emerald-700" : "text-stone-400"}>You {s.me_today ? "✓ today" : "· not yet today"}</span>
      <span className="text-stone-300">|</span>
      <span className={s.partner_today ? "text-emerald-700" : "text-stone-400"}>{b.partner.handle} {s.partner_today ? "✓ today" : "· not yet today"}</span>
      {b.can_cheer && (
        <button data-testid={`buddy-cheer-${b.id}`} onClick={() => act(() => api.post(`/buddies/${b.id}/cheer`), `Cheer sent to ${b.partner.handle}.`)}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition-colors">
          <Megaphone className="w-3 h-3" /> You've got this
        </button>
      )}
      {b.cheered_today && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`buddy-cheered-${b.id}`}><Check className="w-3 h-3" /> Cheered today</span>}
    </div>
  );
}

export function ActiveBuddy({ b, act }) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4" data-testid={`buddy-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <HandleAvatar handle={b.partner.handle} className="mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-stone-900">{b.partner.handle} <span className="text-stone-400 font-normal">· {b.habit_title}</span></div>
          <SharedStreak b={b} />
          <Milestones b={b} />
          <TodayRow b={b} act={act} />
        </div>
        <button data-testid={`buddy-end-${b.id}`} onClick={() => act(() => api.delete(`/buddies/${b.id}`), "Pairing ended. No hard feelings.")} className="text-xs text-stone-400 hover:text-stone-700">End pairing</button>
      </div>
    </div>
  );
}

export function IncomingBuddy({ b, act }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-stone-50 p-3 text-sm" data-testid={`buddy-incoming-${b.id}`}>
      <div className="flex items-center gap-2"><HandleAvatar handle={b.partner.handle} size="sm" /><span><span className="font-semibold">{b.partner.handle}</span> · {b.habit_title}</span></div>
      <div className="flex gap-1.5">
        <button data-testid={`buddy-accept-${b.id}`} onClick={() => act(() => api.post(`/buddies/${b.id}/accept`), `You and ${b.partner.handle} are buddies now.`)} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-700 text-white text-xs font-semibold"><Check className="w-3 h-3" /> Accept</button>
        <button data-testid={`buddy-decline-${b.id}`} onClick={() => act(() => api.delete(`/buddies/${b.id}`))} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone-200 text-stone-700 text-xs font-semibold"><X className="w-3 h-3" /> Not now</button>
      </div>
    </div>
  );
}

export function MatchRow({ m, act }) {
  return (
    <li className="flex items-center justify-between gap-3 text-sm rounded-2xl border border-stone-100 p-3" data-testid={`buddy-match-${m.user_id}`}>
      <div className="flex items-center gap-2">
        <HandleAvatar handle={m.handle} size="sm" />
        <span>
          <span className="font-semibold text-stone-900">{m.handle}</span>{m.is_sample && <SampleTag id={`buddy-${m.user_id}`} className="ml-1.5" />}
          <span className="text-stone-500"> · {m.kind === "stop" ? "Stop: " : ""}{m.habit_title}</span>
        </span>
      </div>
      <button data-testid={`buddy-request-${m.user_id}`} onClick={() => act(() => api.post("/buddies/request", { to_user_id: m.user_id, habit_title: m.my_habit_title }), `Asked ${m.handle} to pair up.`)} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-amber-700"><HandHeart className="w-3 h-3" /> Ask to pair up</button>
    </li>
  );
}

export function MatchesSection({ matches, act }) {
  if (!matches.eligible) return null;
  return (
    <div data-testid="buddies-matches">
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1.5">Working on the same habit as you</div>
      {matches.matches.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="buddies-no-matches">No one with a matching habit name yet. As more members share their habits, matches appear here.</p>
      ) : (
        <ul className="space-y-1.5">
          {matches.matches.map((m) => <MatchRow key={`${m.user_id}-${m.habit_title}`} m={m} act={act} />)}
        </ul>
      )}
    </div>
  );
}
